import { useMemo, useState } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/feature/LoadingState";
import { useCustomers } from "@/hooks/admin/useAdminData";
import type { Customer } from "@/mocks/admin/customers";
import CustomersTable from "./components/CustomersTable";
import AddCustomerModal from "./components/AddCustomerModal";

const statusTabs = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "vip", label: "VIP" },
  { key: "lead", label: "Leads" },
  { key: "dormant", label: "Dormant" },
];

const typeFilters = ["All Types", "Individual", "Business", "Property Mgmt"];

const typeMap: Record<string, string> = {
  Individual: "individual",
  Business: "business",
  "Property Mgmt": "property-mgmt",
};

export default function CustomersPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [activeType, setActiveType] = useState("All Types");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState("");
  const { data: customers, loading, error, refetch } = useCustomers();

  const handleCreated = async (customer: Customer) => {
    await refetch();
    setActiveTab(customer.status);
    setToast(`${customer.name} added`);
    setTimeout(() => setToast(""), 3500);
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: customers.length };
    customers.forEach((cu) => {
      c[cu.status] = (c[cu.status] || 0) + 1;
    });
    return c;
  }, [customers]);

  const filtered = useMemo(() => {
    let list = customers;
    if (activeTab !== "all") list = list.filter((c) => c.status === activeTab);
    if (activeType !== "All Types") {
      const t = typeMap[activeType];
      list = list.filter((c) => c.type === t);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.phone.toLowerCase().includes(q) ||
          c.city.toLowerCase().includes(q) ||
          c.id.toLowerCase().includes(q),
      );
    }
    return list;
  }, [activeTab, activeType, search, customers]);

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-ink-500 mb-2">
              <span>People</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-ink-900 font-semibold">Customers</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 leading-tight">
              Customers
            </h1>
            <p className="text-sm text-ink-500 mt-1.5 max-w-xl">
              Everyone Haulmark has moved or quoted. Click any row for the full history,
              contact details, and active bookings.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line"></i>
              Export
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i>
              Add Customer
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-ink-100 rounded-lg">
          <div className="flex items-center gap-1 px-3 pt-2 border-b border-ink-100 overflow-x-auto scrollbar-thin">
            {statusTabs.map((t) => {
              const active = t.key === activeTab;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`relative px-3 py-3 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                    active ? "text-ink-900" : "text-ink-500 hover:text-ink-700"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {t.label}
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        active ? "bg-ink-900 text-white" : "bg-cream-100 text-ink-600"
                      }`}
                    >
                      {counts[t.key] || 0}
                    </span>
                  </span>
                  {active && (
                    <span className="absolute left-3 right-3 -bottom-px h-0.5 bg-brand-500 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3">
            <div className="relative flex-1 min-w-0">
              <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, phone, or city…"
                className="w-full text-sm pl-10 pr-3 py-2.5 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {typeFilters.map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveType(t)}
                  className={`text-xs font-semibold px-2.5 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                    activeType === t
                      ? "bg-ink-900 text-white"
                      : "bg-cream-100 text-ink-600 hover:bg-ink-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingState message="Loading customers…" />
        ) : error ? (
          <div className="bg-white border border-ink-100 rounded-lg py-20 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 text-red-400 mx-auto mb-3">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <div className="font-display text-lg font-bold text-ink-900">Failed to load customers</div>
            <div className="text-sm text-ink-500 mt-1">{error}</div>
            <button
              onClick={refetch}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Retry
            </button>
          </div>
        ) : (
          <CustomersTable customers={filtered} />
        )}
      </div>

      <AddCustomerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={handleCreated}
      />

      <div
        className={`fixed bottom-6 right-6 z-[60] transition-all duration-300 ${
          toast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md border border-ink-800 flex items-center gap-2 max-w-sm">
          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
            <i className="ri-check-line text-sm"></i>
          </span>
          {toast}
        </div>
      </div>
    </AdminLayout>
  );
}